import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import samhealthLogo from '../../assets/samhealth.png';

const styles = StyleSheet.create({
  page: { flexDirection: 'column', backgroundColor: '#FFFFFF', paddingTop: 20, paddingHorizontal: 20, paddingBottom: 20, fontFamily: 'Helvetica', fontSize: 10, lineHeight: 1.4 },
  frame: { borderWidth: 1.5, borderColor: '#1e4d72', borderStyle: 'solid', borderRadius: 6, paddingTop: 16, paddingHorizontal: 20, paddingBottom: 16, minHeight: '100%' },
  header: { borderBottomWidth: 1, borderBottomColor: '#C7CDD3', borderBottomStyle: 'solid', paddingBottom: 10, marginBottom: 14 },
  orgName: { fontSize: 16, fontWeight: 'bold', color: '#1F2937' },
  orgSub: { fontSize: 9, color: '#4B5563', marginTop: 2 },
  title: { marginTop: 12, fontSize: 12, color: '#111827', textTransform: 'uppercase', letterSpacing: 0.6 },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  metaItem: { fontSize: 9, color: '#374151' },
  subject: { marginTop: 14, fontSize: 11, color: '#111827', fontWeight: 'bold' },
  paragraph: { marginTop: 8, fontSize: 10, color: '#111827', lineHeight: 1.6, textAlign: 'justify' },
  section: { marginTop: 12 },
  sectionHeading: { fontSize: 10, color: '#111827', fontWeight: 'bold', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', borderBottomStyle: 'solid', paddingBottom: 6, marginBottom: 6 },
  highlightBox: { marginTop: 8, borderWidth: 1.2, borderColor: '#1e3a8a', borderStyle: 'solid', borderRadius: 4, backgroundColor: '#F0F6FF', padding: 10 },
  highlightRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  highlightLabel: { fontSize: 9, color: '#1e3a8a', fontWeight: 'bold' },
  highlightValue: { fontSize: 10, color: '#111827', fontWeight: 'bold' },
  highlightPayable: { fontSize: 11, color: '#B91C1C', fontWeight: 'bold' },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: '50%', paddingRight: 8, marginBottom: 6 },
  label: { fontSize: 8, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 },
  value: { fontSize: 10, color: '#111827' },
  footer: { marginTop: 'auto', borderTopWidth: 1, borderTopColor: '#E5E7EB', borderTopStyle: 'solid', paddingTop: 8 },
  footerText: { fontSize: 8, color: '#6B7280', textAlign: 'center' },
});

const formatINR = (n) => `Rs. ${(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => {
  const dd = d?.toDate ? d.toDate() : (d ? new Date(d) : null);
  return dd ? dd.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
};

// Helper function to normalize and clean text (from KYP)
const normalizeText = (str) => {
  if (typeof str !== 'string') return str;
  let s = str.replace(/\s+/g, ' ').trim();
  const fullRep = s.match(/^(.+?)\1+$/);
  if (fullRep) s = fullRep[1].trim();
  return s;
};

// Helper function to aggregate remarks from multiple sources
const aggregateRemarks = (application, additionalRemarks = '') => {
  const remarksSources = [
    application?.workflow?.medicalVerification?.letterRemarks,
    application?.workflow?.medicalVerification?.clinicalNotes,
    application?.workflow?.medicalVerification?.internalRemarks,
    application?.workflow?.dischargeSettlement?.comments,
    additionalRemarks
  ];
  
  const validRemarks = remarksSources
    .filter(remark => remark && remark.toString().trim())
    .map(remark => remark.toString().trim());
    
  return validRemarks.length > 0 
    ? validRemarks.join('. ').replace(/\.\s*\./g, '.').trim()
    : 'Final approval granted based on medical verification and discharge review. All policy terms and conditions apply.';
};

const ProfessionalFALDocument = ({ application, remarks = '', leadGeneratorData }) => {
  const mv = application?.workflow?.medicalVerification || {};
  const ds = application?.workflow?.dischargeSettlement || {};
  const approvedByDoctor = Number(mv.finalApprovedAmount || 0);
  const finalSettlement = Number(ds.finalSettlementAmount ?? approvedByDoctor);
  // Discharge items billed total for patient payable calculation
  const totalBilled = Array.isArray(ds.itemsBreakdown)
    ? ds.itemsBreakdown.reduce((sum, it) => sum + Number(it.billedAmount ?? it.billed ?? 0), 0)
    : 0;
  const patientPayable = Math.max(totalBilled - finalSettlement, 0);
  const totalDeductions = Array.isArray(ds.deductions)
    ? ds.deductions.reduce((a, d) => a + (d?.percentage ? ((approvedByDoctor * (parseFloat(d.amount)||0))/100) : (parseFloat(d.amount)||0)), 0)
    : 0;
  const totalAdjustments = Array.isArray(ds.adjustments)
    ? ds.adjustments.reduce((a, x) => a + (parseFloat(x.amount)||0), 0)
    : 0;
  const lg = leadGeneratorData || application?.leadGenerator || {};
  // Prefer items from medicalVerification.dischargeReview; fallback to
  // workflow.dischargeSettlement.itemsBreakdown when unavailable
  const rawMVItems = Array.isArray(mv?.dischargeReview?.items) ? mv.dischargeReview.items : [];
  const rawDSItems = Array.isArray(application?.workflow?.dischargeSettlement?.itemsBreakdown)
    ? application.workflow.dischargeSettlement.itemsBreakdown
    : [];
  const items = (rawMVItems.length > 0 ? rawMVItems : rawDSItems);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.frame}>
          {/* Header */}
          <View style={styles.header}>
            {/* Logo row: SamHealth on left, Lead Generator on right */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                {/* SamHealth brand logo */}
                <View style={{ width: 90, height: 54, borderWidth: 1, borderColor: '#E5E7EB', borderStyle: 'solid', borderRadius: 4, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' }}>
                  <Image src={samhealthLogo} style={{ width: 86, height: 50 }} />
                </View>
                <View style={{ paddingLeft: 8 }}>
                  <Text style={styles.orgName}>SamHealth</Text>
                  <Text style={styles.orgSub}>Final Approval Letter (Discharge Settlement Confirmation)</Text>
                </View>
              </View>
              {lg?.documents?.entityLogo?.url && (
                <View style={{ width: 90, height: 54, borderWidth: 1, borderColor: '#E5E7EB', borderStyle: 'solid', borderRadius: 4, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' }}>
                  <Image src={lg.documents.entityLogo.url} style={{ width: 86, height: 50 }} />
                </View>
              )}
            </View>
            <Text style={styles.title}>FINAL APPROVAL LETTER</Text>
            <View style={styles.metaRow}>
              <Text style={styles.metaItem}>Case ID: {application?.caseId || application?.applicationId}</Text>
              <Text style={styles.metaItem}>Date: {new Date().toLocaleDateString('en-IN')}</Text>
            </View>
          </View>

          {/* Subject & Intro */}
          <Text style={styles.subject}>Subject: Final Approval of Claim and Settlement Confirmation</Text>
          <Text style={styles.paragraph}>
            This is to confirm that the claim for the below-mentioned patient has been reviewed post discharge and the final settlement amount has been approved as per the details below.
          </Text>

          {/* Patient & Policy Details */}
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>PATIENT & POLICY DETAILS</Text>
            <View style={styles.grid}>
              <View style={styles.cell}><Text style={styles.label}>Patient Name</Text><Text style={styles.value}>{normalizeText(application?.patient?.name || '—')}</Text></View>
              <View style={styles.cell}><Text style={styles.label}>Age / Gender</Text><Text style={styles.value}>{application?.patient?.age ?? '—'} / {application?.patient?.gender || '—'}</Text></View>
              {application?.medical?.diagnosis && (<View style={styles.cell}><Text style={styles.label}>Diagnosis</Text><Text style={styles.value}>{application.medical.diagnosis}</Text></View>)}
              {application?.policy?.number && (<View style={styles.cell}><Text style={styles.label}>Policy Number</Text><Text style={styles.value}>{application.policy.number}</Text></View>)}
              {application?.policy?.insuranceCompany && (<View style={styles.cell}><Text style={styles.label}>Insurance Company</Text><Text style={styles.value}>{application.policy.insuranceCompany}</Text></View>)}
              {application?.medical?.dischargeDate && (<View style={styles.cell}><Text style={styles.label}>Discharge Date</Text><Text style={styles.value}>{fmtDate(application.medical.dischargeDate)}</Text></View>)}
            </View>
          </View>

          {/* Financial Summary */}
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>FINANCIAL SUMMARY</Text>
            <View style={styles.grid}>
              {application?.medical?.estimatedCost && (
                <View style={styles.cell}><Text style={styles.label}>Estimated Cost</Text><Text style={styles.value}>{formatINR(application.medical.estimatedCost)}</Text></View>
              )}
              <View style={styles.cell}><Text style={styles.label}>Deductions</Text><Text style={styles.value}>{formatINR(totalDeductions)}</Text></View>
              <View style={styles.cell}><Text style={styles.label}>Adjustments</Text><Text style={styles.value}>{formatINR(totalAdjustments)}</Text></View>
              {approvedByDoctor !== finalSettlement && (
                <View style={styles.cell}><Text style={styles.label}>Doctor Approved (Pre-Settlement)</Text><Text style={styles.value}>{formatINR(approvedByDoctor)}</Text></View>
              )}
            </View>
            {/* Highlighted Key Figures */}
            <View style={styles.highlightBox}>
              <View style={styles.highlightRow}>
                <Text style={styles.highlightLabel}>Total Billed</Text>
                <Text style={styles.highlightValue}>{formatINR(totalBilled)}</Text>
              </View>
              <View style={styles.highlightRow}>
                <Text style={styles.highlightLabel}>Approved / Final Settlement</Text>
                <Text style={styles.highlightValue}>{formatINR(finalSettlement)}</Text>
              </View>
              <View style={[styles.highlightRow, { borderTopWidth: 1, borderTopColor: '#1e3a8a', borderTopStyle: 'solid', paddingTop: 6, marginTop: 4 }]}> 
                <Text style={styles.highlightLabel}>PATIENT PAYABLE</Text>
                <Text style={styles.highlightPayable}>{formatINR(patientPayable)}</Text>
              </View>
            </View>
          </View>

          {/* Room & ICU Eligibility (only if data present) */}
          {(application?.medical?.roomCategory || mv?.roomEligibility || application?.workflow?.policyVerification?.roomEligibility) && (
            <View style={styles.section}>
              <Text style={styles.sectionHeading}>ROOM & ICU ELIGIBILITY</Text>
              <View style={styles.grid}>
                {application?.medical?.roomCategory && (<View style={styles.cell}><Text style={styles.label}>Room Category</Text><Text style={styles.value}>{application.medical.roomCategory}</Text></View>)}
                {mv?.roomEligibility?.roomType && (<View style={styles.cell}><Text style={styles.label}>Eligible Room Type</Text><Text style={styles.value}>{mv.roomEligibility.roomType}</Text></View>)}
                {mv?.roomEligibility?.roomAmountPerDay && (<View style={styles.cell}><Text style={styles.label}>Room Amount / Day</Text><Text style={styles.value}>{formatINR(mv.roomEligibility.roomAmountPerDay)}</Text></View>)}
                {mv?.roomEligibility?.icuAmountPerDay && (<View style={styles.cell}><Text style={styles.label}>ICU Amount / Day</Text><Text style={styles.value}>{formatINR(mv.roomEligibility.icuAmountPerDay)}</Text></View>)}
              </View>
            </View>
          )}

          {/* Discharge Review Summary */}
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>DISCHARGE REVIEW SUMMARY</Text>
            <View style={styles.grid}>
              <View style={styles.cell}><Text style={styles.label}>Items Reviewed</Text><Text style={styles.value}>{mv?.dischargeReview?.items?.length ?? (items?.length || 0)}</Text></View>
              <View style={styles.cell}><Text style={styles.label}>Deductions</Text><Text style={styles.value}>{formatINR(totalDeductions)}</Text></View>
              <View style={styles.cell}><Text style={styles.label}>Adjustments</Text><Text style={styles.value}>{formatINR(totalAdjustments)}</Text></View>
              <View style={styles.cell}><Text style={styles.label}>Variance (Billed - Final)</Text><Text style={styles.value}>{formatINR(totalBilled - finalSettlement)}</Text></View>
            </View>
          </View>

          {/* Discharge Items Details (mirrors app Discharge Summary Review table) */}
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>DISCHARGE ITEMS DETAILS</Text>
            <View style={{ borderWidth: 1, borderColor: '#E5E7EB', borderStyle: 'solid', borderRadius: 4 }}>
              {/* Header */}
              <View style={{ flexDirection: 'row', backgroundColor: '#F3F4F6', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', borderBottomStyle: 'solid' }}>
                <Text style={{ flex: 2, padding: 6, fontSize: 9, fontWeight: 'bold' }}>ITEM/SERVICE</Text>
                <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold' }}>CATEGORY</Text>
                <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'right' }}>RATE/UNIT</Text>
                <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'right' }}>ELIG. RATE/UNIT</Text>
                <Text style={{ flex: 0.7, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'center' }}>QTY</Text>
                <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'right' }}>BILLED AMOUNT</Text>
                <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'right' }}>ELIGIBLE AMOUNT</Text>
                <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'right' }}>VARIANCE</Text>
                <Text style={{ flex: 1.5, padding: 6, fontSize: 9, fontWeight: 'bold' }}>REMARKS</Text>
                <Text style={{ flex: 0.8, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'center' }}>APPROVED</Text>
              </View>
              {/* Rows */}
              {Array.isArray(items) && items.length > 0 ? (
                <>
                  {items.map((raw, idx) => {
                    const name = raw.itemName || raw.name || raw.title || 'Item';
                    const category = raw.category || raw.type || '-';
                    const qty = Number(raw.quantity || raw.qty || 0);
                    const unitRate = Number(raw.unitRate || raw.rate || raw.unit || 0);
                    const eligibleUnitRate = Number(raw.eligibleUnitRate ?? raw.eligibleRate ?? unitRate ?? 0);
                    const billed = Number((raw.billedAmount ?? raw.billed ?? (unitRate * qty)) ?? 0);
                    // If coming from computed itemsBreakdown, prefer finalPayable as eligible amount
                    const eligible = Number((raw.finalPayable ?? raw.eligibleAmount ?? (eligibleUnitRate * qty)) ?? 0);
                    const variance = billed - eligible;
                    const remarksCell = (raw.remarks ?? raw.comment ?? '-').toString();
                    const approved = raw.approved === true || raw.status === 'approved' || raw.isApproved === true;
                    return (
                      <View key={idx} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', borderBottomStyle: 'solid' }}>
                        <Text style={{ flex: 2, padding: 6, fontSize: 9 }}>{name}</Text>
                        <Text style={{ flex: 1, padding: 6, fontSize: 9 }}>{category}</Text>
                        <Text style={{ flex: 1, padding: 6, fontSize: 9, textAlign: 'right' }}>{formatINR(unitRate)}</Text>
                        <Text style={{ flex: 1, padding: 6, fontSize: 9, textAlign: 'right' }}>{formatINR(eligibleUnitRate)}</Text>
                        <Text style={{ flex: 0.7, padding: 6, fontSize: 9, textAlign: 'center' }}>{qty}</Text>
                        <Text style={{ flex: 1, padding: 6, fontSize: 9, textAlign: 'right' }}>{formatINR(billed)}</Text>
                        <Text style={{ flex: 1, padding: 6, fontSize: 9, textAlign: 'right' }}>{formatINR(eligible)}</Text>
                        <Text style={{ flex: 1, padding: 6, fontSize: 9, textAlign: 'right', color: variance > 0 ? 'red' : '#111827' }}>{formatINR(variance)}</Text>
                        <Text style={{ flex: 1.5, padding: 6, fontSize: 9 }}>{remarksCell}</Text>
                        <Text style={{ flex: 0.8, padding: 6, fontSize: 9, textAlign: 'center' }}>{approved ? 'Yes' : 'No'}</Text>
                      </View>
                    );
                  })}
                  {/* Totals */}
                  {(() => {
                    const totalBilled = items.reduce((a, raw) => a + Number((raw.billedAmount ?? (Number(raw.unitRate || 0) * Number(raw.quantity || 0))) ?? 0), 0);
                    const totalEligible = items.reduce((a, raw) => {
                      const qty = Number(raw.quantity || 0);
                      const unitRate = Number(raw.unitRate || 0);
                      const eligibleUnitRate = Number(raw.eligibleUnitRate ?? raw.eligibleRate ?? unitRate ?? 0);
                      const eligible = Number((raw.eligibleAmount ?? (eligibleUnitRate * qty)) ?? 0);
                      return a + eligible;
                    }, 0);
                    const totalVariance = totalBilled - totalEligible;
                    return (
                      <View style={{ flexDirection: 'row' }}>
                        <Text style={{ flex: 2, padding: 6, fontSize: 9, fontWeight: 'bold' }}>Total</Text>
                        <Text style={{ flex: 1 }} />
                        <Text style={{ flex: 1 }} />
                        <Text style={{ flex: 1 }} />
                        <Text style={{ flex: 0.7 }} />
                        <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'right' }}>{formatINR(totalBilled)}</Text>
                        <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'right' }}>{formatINR(totalEligible)}</Text>
                        <Text style={{ flex: 1, padding: 6, fontSize: 9, fontWeight: 'bold', textAlign: 'right', color: totalVariance > 0 ? 'red' : '#111827' }}>{formatINR(totalVariance)}</Text>
                        <Text style={{ flex: 1.5 }} />
                        <Text style={{ flex: 0.8 }} />
                      </View>
                    );
                  })()}
                </>
              ) : (
                <View style={{ flexDirection: 'row' }}>
                  <Text style={{ flex: 2, padding: 6, fontSize: 9 }}>No discharge items available</Text>
                  <Text style={{ flex: 1 }} />
                  <Text style={{ flex: 1 }} />
                  <Text style={{ flex: 1 }} />
                  <Text style={{ flex: 0.7 }} />
                  <Text style={{ flex: 1 }} />
                  <Text style={{ flex: 1 }} />
                  <Text style={{ flex: 1 }} />
                  <Text style={{ flex: 1.5 }} />
                  <Text style={{ flex: 0.8 }} />
                </View>
              )}
            </View>
          </View>

          {/* Notes */}
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>NOTES</Text>
            <Text style={styles.paragraph}>{aggregateRemarks(application, remarks)}</Text>
          </View>

          {/* Final Verification Checklist (KYP-style) */}
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>FINAL VERIFICATION CHECKLIST</Text>
            <View style={{ backgroundColor: '#f9f9f9', padding: 8, borderRadius: 4 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
                <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
                <Text style={{ fontSize: 10, flex: 1 }}>Medical documentation review completed</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
                <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
                <Text style={{ fontSize: 10, flex: 1 }}>Policy coverage verification completed</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
                <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
                <Text style={{ fontSize: 10, flex: 1 }}>Discharge summary and bills verified</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
                <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
                <Text style={{ fontSize: 10, flex: 1 }}>Co-payment and deductibles calculated</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
                <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
                <Text style={{ fontSize: 10, flex: 1 }}>Final settlement amount approved</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
                <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
                <Text style={{ fontSize: 10, flex: 1 }}>Authorization cleared for payment</Text>
              </View>
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>This letter is issued as the final approval confirmation and may be shared with the hospital billing team for discharge settlement. Disbursal will proceed as per standard process and timelines.</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ProfessionalFALDocument;
